import { useCallback, useEffect, useState } from 'react';
import type { Page } from '../types';
import { workspaces } from './seed';
import type { WorkspaceId } from './seed';

export interface Route {
  workspaceId: WorkspaceId;
  page: Page;
  id?: string;
}

const FALLBACK_WORKSPACE_ID: WorkspaceId = 'brzoza';

const workspacePages: Page[] = [
  'calendar',
  'ideas',
  'materials',
  'brand',
  'reports',
  'allowance',
  'settings',
  'onboarding',
  'review',
  'post',
  'welcome',
];

export function parseRoute(hash: string): Route {
  const hashPathSegments = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  const [firstSegment, secondSegment, thirdSegment] = hashPathSegments;
  if (firstSegment === 'agency')
    return {
      workspaceId: (secondSegment as WorkspaceId) || FALLBACK_WORKSPACE_ID,
      page: 'agencyHome',
    };
  if (firstSegment === 'platform')
    return { workspaceId: FALLBACK_WORKSPACE_ID, page: 'platformHome' };
  const isKnownWorkspace = workspaces.some((workspace) => workspace.id === firstSegment);
  const workspaceId = (isKnownWorkspace ? firstSegment : FALLBACK_WORKSPACE_ID) as WorkspaceId;
  const isKnownPage = workspacePages.includes(secondSegment as Page);
  const page = (isKnownPage ? secondSegment : 'calendar') as Page;
  const id = thirdSegment ? decodeURIComponent(thirdSegment) : undefined;
  return { workspaceId, page, id };
}

export function routeToHash(route: Route) {
  if (route.page === 'agencyHome') return '#/agency';
  if (route.page === 'platformHome') return '#/platform';
  const idSuffix = route.id ? `/${encodeURIComponent(route.id)}` : '';
  return `#/${route.workspaceId}/${route.page}${idSuffix}`;
}

export function useHashRoute() {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  useEffect(() => {
    const syncRouteFromHash = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', syncRouteFromHash);
    return () => window.removeEventListener('hashchange', syncRouteFromHash);
  }, []);
  const navigate = useCallback((next: Partial<Route>, options?: { replace?: boolean }) => {
    const targetHash = routeToHash({ ...parseRoute(window.location.hash), id: undefined, ...next });
    if (targetHash === window.location.hash) return;
    if (options?.replace) {
      window.history.replaceState(null, '', targetHash);
      setRoute(parseRoute(targetHash));
    } else {
      window.location.hash = targetHash;
    }
  }, []);
  return { route, navigate };
}
